/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import {
  Box,
  Breadcrumbs,
  Chip,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import type {
  GeoWebPluginInstance,
  ScopeName,
} from '@opengeoweb/plugin-interface';
import { ToolContainerDraggable } from '@opengeoweb/shared';
import { usePluginCatalog } from '../../api';
import { usePluginsTranslation } from '../../utils/i18n';
import { usePlugin } from '../PluginContext';
import { useDebugLog } from './PluginDebugContext';
import type { DebugLogEntry, PluginCatalogEntry } from './pluginDebugTypes';
import { EntryList } from './sections/EntryList';
import { PluginInfo } from './sections/PluginInfo';
import { ScopeStatesSection } from './sections/PluginScope';
import { PluginStateSection } from './sections/PluginState';
import { Sidebar } from './sections/Sidebar';
import type { SelectedInstance } from './types';

export interface PluginDebugProps {
  logEntries?: DebugLogEntry[];
  onClearLog?: () => void;
  plugins?: PluginCatalogEntry[];
  pluginInstances?: Record<string, GeoWebPluginInstance>;
  onClose?: () => void;
}

const usePluginCatalogEntries = (): PluginCatalogEntry[] => {
  const { loadedPlugins, pluginInstances } = usePlugin();
  const { data: catalog } = usePluginCatalog();

  return useMemo(() => {
    const entries = new Map<string, PluginCatalogEntry>();

    // Process loaded plugins (keys may be "pluginId@version")
    for (const pluginKey of Object.keys(loadedPlugins)) {
      const atIndex = pluginKey.lastIndexOf('@');
      const baseId = atIndex > 0 ? pluginKey.slice(0, atIndex) : pluginKey;
      const loadedVersion =
        atIndex > 0 ? pluginKey.slice(atIndex + 1) : undefined;

      const loaded = loadedPlugins[pluginKey];
      const meta = catalog?.[baseId];

      const instanceIds = Object.entries(pluginInstances)
        .filter(([, inst]) => inst.plugin?.name === loaded?.name)
        .map(([id]) => id);

      entries.set(baseId, {
        pluginId: pluginKey,
        name: loaded?.name ?? meta?.name ?? baseId,
        type: loaded?.type ?? meta?.type,
        version: loadedVersion ?? meta?.version,
        description: meta?.description,
        scopes: loaded?.scopes ?? [],
        instanceIds,
      });
    }

    // Add catalog entries not already covered by loaded plugins
    for (const catalogId of Object.keys(catalog ?? {})) {
      if (!entries.has(catalogId)) {
        const meta = catalog![catalogId];
        entries.set(catalogId, {
          pluginId: catalogId,
          name: meta.name ?? catalogId,
          type: meta.type,
          version: meta.version,
          description: meta.description,
          scopes: [],
          instanceIds: [],
        });
      }
    }

    return Array.from(entries.values());
  }, [loadedPlugins, pluginInstances, catalog]);
};

export const PluginDebug: React.FC<PluginDebugProps> = ({
  logEntries: logEntriesProp,
  onClearLog: onClearLogProp,
  plugins: pluginsProp,
  pluginInstances: pluginInstancesProp,
  onClose,
}) => {
  const { t } = usePluginsTranslation();
  const { pluginInstances: contextInstances } = usePlugin();
  const { addDebugEntry, clearDebugEntries, debugEntries, managers } =
    useDebugLog();
  const derivedPlugins = usePluginCatalogEntries();

  const plugins = pluginsProp ?? derivedPlugins;
  const logEntries = logEntriesProp ?? debugEntries;
  const onClearLog = onClearLogProp ?? clearDebugEntries;
  const pluginInstances = useMemo(
    () => pluginInstancesProp ?? contextInstances ?? {},
    [pluginInstancesProp, contextInstances],
  );

  // Subscribe to plugin-internal state changes for all registered instances
  // so they appear in the activity log.
  useEffect(() => {
    const instanceIds = Object.keys(pluginInstances);
    const unsubscribes: (() => void)[] = [];

    for (const instanceId of instanceIds) {
      const instance = pluginInstances[instanceId];
      if (!instance?.onPluginStateChange) {
        // eslint-disable-next-line no-continue
        continue;
      }

      const matchingPlugin = plugins.find((plugin) =>
        plugin.instanceIds.includes(instanceId),
      );

      // Skip the synchronous initial callback
      let isFirst = true;
      const unsub = instance.onPluginStateChange((state) => {
        if (isFirst) {
          isFirst = false;
          return;
        }
        addDebugEntry({
          source: 'plugin',
          type: 'internal-state',
          instanceId,
          pluginId: matchingPlugin?.pluginId ?? instanceId,
          data: state,
        });
      });
      unsubscribes.push(unsub);
    }

    return (): void => {
      unsubscribes.forEach((unsub) => unsub());
    };
  }, [pluginInstances, plugins, addDebugEntry]);

  // Subscribe to per-instance PluginManager debug streams. This captures
  // scope events (`sendEvent`) and scope-state broadcasts, regardless of
  // whether the origin is a plugin or GeoWeb itself.
  useEffect(() => {
    const unsubscribes: (() => void)[] = [];

    for (const instanceId of Object.keys(managers)) {
      const manager = managers[instanceId];
      const matchingPlugin = plugins.find((plugin) =>
        plugin.instanceIds.includes(instanceId),
      );
      const pluginId = matchingPlugin?.pluginId ?? instanceId;

      const unsub = manager.onDebugEvent((debugEvent) => {
        const scope = debugEvent.scope as ScopeName;
        if (debugEvent.type === 'event') {
          addDebugEntry({
            source: debugEvent.source,
            type: 'event',
            scope,
            instanceId,
            pluginId,
            data: debugEvent.data,
          });
          return;
        }
        addDebugEntry({
          source: debugEvent.source,
          type: 'scope-update',
          scope,
          instanceId,
          pluginId,
          data: debugEvent.data,
        });
      });
      unsubscribes.push(unsub);
    }

    return (): void => {
      unsubscribes.forEach((unsub) => unsub());
    };
  }, [managers, plugins, addDebugEntry]);

  const [selected, setSelected] = useState<SelectedInstance>(null);

  const handleClose = (): void => {
    onClose?.();
    setSelected(null);
  };

  const selectedPlugin = selected
    ? plugins.find((plugin) => plugin.pluginId === selected.pluginId)
    : undefined;
  const selectedInstance = selected
    ? (pluginInstances[selected.instanceId] ?? null)
    : null;

  const totalInstances = plugins.reduce(
    (acc, plugin) => acc + plugin.instanceIds.length,
    0,
  );

  const pluginInstanceIds = Object.keys(pluginInstances);

  return (
    <>
      {/* Debug dialog */}
      <ToolContainerDraggable
        initialMaxHeight={500}
        onClose={handleClose}
        title={t('plugin-debug-inspector-title')}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
            pb: 1,
          }}
        >
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
            <Typography component="span" variant="h6">
              {t('plugin-debug-inspector')}
            </Typography>
            <Chip
              label={t('plugins-count', { count: plugins.length })}
              size="small"
              variant="outlined"
            />
            <Chip
              label={t('instances-count', { count: totalInstances })}
              size="small"
              variant="outlined"
            />
            <Chip
              label={t('events-count', { count: logEntries.length })}
              size="small"
              variant="outlined"
            />
          </Box>
          <Box>
            {!!onClearLog && (
              <Tooltip title={t('clear-log')}>
                <IconButton
                  data-testid="plugin-debug-clear-log"
                  onClick={onClearLog}
                  size="small"
                >
                  <Typography component="span" sx={{ fontSize: 16 }}>
                    🗑
                  </Typography>
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </DialogTitle>

        <Divider />

        {/* Breadcrumb navigation */}
        <Box sx={{ px: 3, py: 1 }}>
          <Breadcrumbs>
            <Link
              color={!selected ? 'text.primary' : 'inherit'}
              component="button"
              onClick={() => setSelected(null)}
              sx={{ cursor: 'pointer' }}
              underline="hover"
            >
              {t('overview')}
            </Link>
            {selected && selectedPlugin ? (
              <Typography color="text.primary" variant="body2">
                {selectedPlugin.name} / {selected.instanceId}
              </Typography>
            ) : null}
          </Breadcrumbs>
        </Box>

        <Divider />

        {/* Split-panel content */}
        <DialogContent sx={{ display: 'flex', padding: 0 }}>
          {/* Left sidebar */}
          <Box
            sx={{
              borderColor: 'divider',
              borderRight: 1,
              flexShrink: 0,
              overflowY: 'auto',
              width: 280,
            }}
          >
            <Sidebar
              logEntries={logEntries}
              onSelect={setSelected}
              plugins={plugins}
              selected={selected}
            />
          </Box>

          {/* Right content panel - split into two columns */}
          <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
            {/* Left column: Info & States */}
            <Box
              sx={{
                borderColor: 'divider',
                borderRight: 1,
                flex: 1,
                overflowY: 'auto',
              }}
            >
              {selected && selectedPlugin ? (
                /* Instance detail view */
                <>
                  <PluginInfo plugin={selectedPlugin} />
                  <Divider />
                  <ScopeStatesSection
                    instanceId={selected.instanceId}
                    logEntries={logEntries}
                  />
                  <Divider />
                  <PluginStateSection
                    instance={selectedInstance}
                    instanceId={selected.instanceId}
                  />
                </>
              ) : (
                /* Overview */
                <>
                  <ScopeStatesSection logEntries={logEntries} />
                  <Divider />
                  {pluginInstanceIds.length > 0 ? (
                    <Box
                      data-testid="plugin-debug-all-plugin-states"
                      sx={{ px: 2, py: 1 }}
                    >
                      <Typography gutterBottom variant="subtitle2">
                        {t('plugin-internal-states')}
                      </Typography>
                      {pluginInstanceIds.map((id) => (
                        <PluginStateSection
                          instance={pluginInstances[id]}
                          instanceId={id}
                          key={id}
                        />
                      ))}
                    </Box>
                  ) : null}
                </>
              )}
            </Box>

            {/* Right column: Activity */}
            <Box sx={{ flex: 1, overflowY: 'auto' }}>
              {selected && selectedPlugin ? (
                <EntryList
                  containerTestId="plugin-debug-activity-log"
                  entries={logEntries.filter(
                    (entry) => entry.instanceId === selected.instanceId,
                  )}
                  entryTestIdPrefix="plugin-debug-log-entry"
                  title={
                    <>
                      {t('activity-for')} <code>{selected.instanceId}</code>
                    </>
                  }
                  variant="activity"
                />
              ) : (
                <EntryList
                  containerTestId="plugin-debug-recent-activity"
                  entries={logEntries.slice(0, 15)}
                  entryTestIdPrefix="plugin-debug-recent-entry"
                  title={t('recent-activity-total', {
                    count: logEntries.length,
                  })}
                  variant="recent"
                />
              )}
            </Box>
          </Box>
        </DialogContent>
      </ToolContainerDraggable>
    </>
  );
};
